import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import {
  decodeTime,
  formatTime,
  isNumber,
} from 'altrac-conversions'
import moment from 'moment'
import styles from '../../../../../css/timeblock.module.css'

const MINUTES_MAXIMUM = 7 * 24 * 60

const getHrDropdown = () => {
  const array = []
  for (let i = 0; i < 168; i++) {
    array.push({ key: i, value: i, str: `${i} Hrs` })
  }
  return array
}
const getMinDropdown = (min = 0, max = 60, extra = '') => {
  const array = []
  for (let i = min; i < max; i++) {
    array.push({ key: i, value: i, str: `${i} Mins${extra}` })
  }
  return array
}

const scheduleRing = (value) => {
  let result = value
  if (value >= 10080) result = value - 10080
  else if (value < 0) result = value + 10080

  if (result < 0 || result >= 10080) throw new Error(`Input value bad ${result}`)
  else return result & 0xFFFF
}

const getWeekDays = () => [
  { str: 'Sunday', value: 0, key: 0 },
  { str: 'Monday', value: 1, key: 1 },
  { str: 'Tuesday', value: 2, key: 2 },
  { str: 'Wednesday', value: 3, key: 3 },
  { str: 'Thursday', value: 4, key: 4 },
  { str: 'Friday', value: 5, key: 5 },
  { str: 'Saturday', value: 6, key: 6 },
]

function formatDate (date) {
  const d = new Date(date)
  let month = `${d.getMonth() + 1}`
  let day = `${d.getDate()}`
  const year = d.getFullYear()

  if (month.length < 2) month = `0${month}`
  if (day.length < 2) day = `0${day}`

  return [year, month, day].join('-')
}

function formatHourMin (date) {
  const d = new Date(date)
  let hours = `${d.getHours()}`
  let minutes = `${d.getMinutes()}`

  if (hours.length < 2) hours = `0${hours}`
  if (minutes.length < 2) minutes = `0${minutes}`

  return [hours, minutes].join(':')
}

const Eventform = ({
  updateIndex,
  timeblocks,
  outletID = '1',
  eventFormDay = (new Date()).getDay(),
  addEvent,
  updateEvent,
  removeEvent
}) => {
  let day = eventFormDay
  let hr = (new Date()).getHours()
  let min = Math.ceil(((new Date()).getMinutes() + 1) / 15) * 15
  if (min >= 60) {
    min = 15
    hr += 1
  }
  let startTime = formatTime(hr, min)
  let duration = 120
  let flags = []
  let fertigationDifference = [0, 0]
  let noLater = 0
  let noLaterEnabled = false
  const checked = [false, false, false, false, false, false, false]
  checked[day] = true

  if (
    updateIndex >= 0 &&
    Array.isArray(timeblocks[outletID]) &&
    updateIndex < timeblocks[outletID].length
  ) {
    const event = timeblocks[outletID][updateIndex]
    const offset = (new Date()).getTimezoneOffset()
    let startOffset = event.start - offset
    let stopOffset = event.stop - offset
    startOffset = scheduleRing(startOffset)
    stopOffset = scheduleRing(stopOffset)
    stopOffset = startOffset > stopOffset ? (stopOffset + MINUTES_MAXIMUM) : stopOffset
    day = Math.floor((startOffset) / (24 * 60))
    hr = Math.floor(startOffset / 60) - ((Math.floor(startOffset / (24 * 60))) * 24)
    min = startOffset % 60
    startTime = formatTime(hr, min)
    duration = stopOffset - startOffset
    flags = event.flags
    fertigationDifference = event.fertigationDifference
    noLater = isNumber(event.noLater) && Number(event.noLater) > 0 ? new Date(Number(event.noLater) * 1000) : new Date()
    noLaterEnabled = isNumber(event.noLater) && Number(event.noLater) > 0
  }

  const durationHr = Math.floor(duration / 60)
  const durationMin = duration % 60

  const [dayState, setDayState] = useState(day)
  const [hrState, setHrState] = useState(hr)
  const [minState, setMinState] = useState(min)
  const [startTimeState, setStartTimeState] = useState(startTime)
  const [startTimeValid, setStartTimeValid] = useState(true)
  const [durationState, setDurationState] = useState(duration)
  const [durationHrState, setDurationHrState] = useState(durationHr)
  const [durationMinState, setDurationMinState] = useState(durationMin)
  const [durationValid, setDurationValid] = useState(duration > 0)
  const [flagsState, setFlagsState] = useState(flags)
  const [fertigationDifferenceState, setFertigationDifferenceState] = useState(fertigationDifference)
  const [fertigationValid, setFertigationValid] = useState(true)
  const [noLaterState, setNoLaterState] = useState(noLater)
  const [noLaterEnabledState, setNoLaterEnabledState] = useState(noLaterEnabled)
  const [dayValid, setDayValid] = useState(true)
  const [checkedState, setCheckedState] = useState(checked)

  const createEvent = useCallback(() => {
    if (dayState > 6 || dayState < 0) return
    const events = []
    checkedState.forEach((d, idx) => {
      if (d) {
        events.push({
          day: idx,
          hr: hrState,
          min: minState,
          duration: durationState,
          outletID,
          flags: flagsState,
          fertigationDifference: fertigationDifferenceState,
          noLater: noLaterEnabledState ? Math.round(noLaterState.getTime() / 1000) : 0,
        })
      }
    })

    addEvent(events)
  }, [dayState, hrState, minState, durationState, outletID, flagsState, fertigationDifferenceState, noLaterEnabledState, noLaterState, checkedState, addEvent])

  const modifyEvent = useCallback((key) => {
    if (dayState > 6 || dayState < 0) return
    updateEvent({
      key,
      day: dayState,
      hr: hrState,
      min: minState,
      duration: durationState,
      outletID,
      flags: flagsState,
      fertigationDifference: fertigationDifferenceState,
      noLater: noLaterEnabledState ? Math.round(noLaterState.getTime() / 1000) : 0,
    })
  }, [dayState, hrState, minState, durationState, outletID, flagsState, fertigationDifferenceState, noLaterEnabledState, noLaterState, updateEvent])

  const deleteEvent = useCallback((updateKey) => {
    removeEvent(updateKey)
  }, [removeEvent])

  const handleStartTime = useCallback((event) => {
    setHrState(Number(event.target.value))
  }, [])

  const handleStartTimeChange = useCallback((event) => {
    const time = event.target.value
    if (!time || typeof time !== 'string') return
    if (time.length > 8) {
      setStartTimeState(event.target.value)
      setStartTimeValid(false)
      return
    }

    let newStartTimeValid = false
    if (
      time.match(/^([0-9]|0[0-9]|1[0-9]|2[0-3]):?[0-5][0-9]$/)
    ) {
      const hr = Number(time.slice(0, time.length < 4 ? 1 : 2))
      const min = Number(time.slice(-2))
      newStartTimeValid = true
      if (hr >= 0 && hr <= 23 && min >= 0 && min <= 59) {
        setStartTimeState(time)
        setStartTimeValid(newStartTimeValid)
        setHrState(hr)
        setMinState(min)
        return
      }
    }
    setStartTimeState(event.target.value)
    setStartTimeValid(false)
  }, [])

  const handleDurationHrChange = useCallback((event) => {
    const durationHr = Number(event.target.value)
    if (durationHr >= 0 && durationHr < 168) {
      const newDuration = durationHr * 60 + durationMinState
      setDurationHrState(durationHr)
      setDurationState(newDuration)
      setDurationValid(newDuration > 0)
      setFertigationValid(fertigationDifferenceState[0] + fertigationDifferenceState[1] < newDuration)
    }
  }, [durationMinState, fertigationDifferenceState])

  const handleDurationMinChange = useCallback((event) => {
    const durationMin = Number(event.target.value)
    if (durationMin >= 0 && durationMin < 60) {
      const newDuration = durationHrState * 60 + durationMin
      setDurationMinState(durationMin)
      setDurationState(newDuration)
      setDurationValid(newDuration > 0)
      setFertigationValid(fertigationDifferenceState[0] + fertigationDifferenceState[1] < newDuration)
    }
  }, [durationHrState, fertigationDifferenceState])

  const handleDateChange = useCallback((event) => {
    setDayState(Number(event.target.value))
  }, [])

  const handleDateChangeCheck = useCallback((event) => {
    const newChecked = [...checkedState]
    let newDay = dayState
    let newDayValid = true

    switch (event.target.name) {
      case 'Sunday':
        newChecked[0] = event.target.checked
        if (event.target.checked) newDay = 0
        break
      case 'Monday':
        newChecked[1] = event.target.checked
        if (event.target.checked) newDay = 1
        break
      case 'Tuesday':
        newChecked[2] = event.target.checked
        if (event.target.checked) newDay = 2
        break
      case 'Wednesday':
        newChecked[3] = event.target.checked
        if (event.target.checked) newDay = 3
        break
      case 'Thursday':
        newChecked[4] = event.target.checked
        if (event.target.checked) newDay = 4
        break
      case 'Friday':
        newChecked[5] = event.target.checked
        if (event.target.checked) newDay = 5
        break
      case 'Saturday':
        newChecked[6] = event.target.checked
        if (event.target.checked) newDay = 6
        break
      default:
        break
    }

    if (!event.target.checked) {
      if (newChecked.findIndex((i) => i === true) >= 0) {
        newDay = newChecked.findIndex((i) => i === true)
      } else {
        newDay = -1
        newDayValid = false
      }
    }
    setCheckedState(newChecked)
    setDayState(newDay)
    setDayValid(newDayValid)
  }, [checkedState, dayState])

  const handleFlagsChange = useCallback((event) => {
    setFlagsState([event.target.value])
  }, [])

  const handleFertigationDifference = useCallback((event) => {
    let newFertigationDifference = [...fertigationDifferenceState]
    if (!Array.isArray(newFertigationDifference)) {
      newFertigationDifference = []
    }
    while (newFertigationDifference.length < 2) {
      newFertigationDifference.push(0)
    }
    switch (event.target.id) {
      case 'fertigationDifference0':
        newFertigationDifference.splice(0, 1, Number(event.target.value))
        break
      case 'fertigationDifference1':
        newFertigationDifference.splice(1, 1, Number(event.target.value))
        break
      default:
        break
    }

    setFertigationDifferenceState(newFertigationDifference)
    setFertigationValid(newFertigationDifference[0] + newFertigationDifference[1] < durationState)
  }, [fertigationDifferenceState, durationState])

  const handleNoLaterEnabled = useCallback((event) => {
    const newNoLaterEnabled = event.target.value === 'true'
    if (!noLaterEnabledState && newNoLaterEnabled) {
      setNoLaterState(moment().add(8, 'days').startOf('day').toDate())
    } else if (noLaterEnabledState && !newNoLaterEnabled) {
      setNoLaterState(0)
    }
    setNoLaterEnabledState(newNoLaterEnabled)
  }, [noLaterEnabledState])

  const handleNoLaterChanged = useCallback((event) => {
    const newNoLater = new Date(noLaterState)
    switch (event.target.id) {
      case 'noLaterDate': {
        const ymd = event.target.value.split('-')
        newNoLater.setFullYear(Number(ymd[0]))
        newNoLater.setMonth(Number(ymd[1]) - 1)
        newNoLater.setDate(Number(ymd[2]))
        break
      }
      case 'noLaterTime': {
        const hm = event.target.value.split(':')
        newNoLater.setHours(Number(hm[0]), Number(hm[1]))
        break
      }
      default:
        break
    }
    setNoLaterState(newNoLater)
  }, [noLaterState])

  let dayOK = dayState
  if (dayState > 6 || dayState < 0) dayOK = 0
  const decoded = decodeTime(
    dayOK * 24 * 60 + hrState * 60 + minState + durationState,
    0
  )
  let stopTime = decoded.stop.human
  if (checkedState.filter((d) => d === true).length !== 1) {
    stopTime = stopTime.substr(4)
  }

  const fertigationDuration = durationState - fertigationDifferenceState[0] - fertigationDifferenceState[1]

  return (
    <div className={`${styles.event_form} ${styles.inputArea}`}>
      <div className={styles.event_inputs}>
        {updateIndex >= 0
          ? (
            <div className={styles.event_inputs_label}>
              Start Day
              <div className={styles.event_day_select}>
                <select value={dayState} onChange={handleDateChange}>
                  {getWeekDays().map((d) => (
                    (<option key={d.key} value={d.value}>{d.str}</option>)
                  ))}
                </select>
              </div>
            </div>
            )
          : (
            <div>
              Select Days
              <div className={styles.event_inputs_check_area}>
                {checkedState.map((dayChecked, i) => {
                  let dayOfWeek = 'Sunday'
                  switch (i) {
                    case 0:
                      dayOfWeek = 'Sunday'
                      break
                    case 1:
                      dayOfWeek = 'Monday'
                      break
                    case 2:
                      dayOfWeek = 'Tuesday'
                      break
                    case 3:
                      dayOfWeek = 'Wednesday'
                      break
                    case 4:
                      dayOfWeek = 'Thursday'
                      break
                    case 5:
                      dayOfWeek = 'Friday'
                      break
                    case 6:
                      dayOfWeek = 'Saturday'
                      break
                    default:
                      break
                  }
                  return (
                    <label key={dayOfWeek} className={styles.event_inputs_check_container}>
                      <input type='checkbox' name={dayOfWeek} value={dayOfWeek} id={`day-check-${dayOfWeek}`} checked={dayChecked} onChange={handleDateChangeCheck} />
                      <span>{dayOfWeek.substr(0, 3)}</span>
                    </label>
                  )
                })}
              </div>
            </div>
            )}
        <div className={styles.event_inputs_label}>
          Start Time
          <input type='time' value={startTimeState} onChange={handleStartTimeChange} name='time_input' />
        </div>
        <div className={styles.event_inputs_label}>
          Duration
          <div className={styles.duration}>
            <select id='duration_hr' value={durationHrState} onChange={handleDurationHrChange}>
              {getHrDropdown().map((d) => (
                (<option key={d.key} value={d.value}>{d.str}</option>)
              ))}
            </select>
            &nbsp;
            <select id='duration_min' value={durationMinState} onChange={handleDurationMinChange}>
              {getMinDropdown().map((d) => (
                (<option key={d.key} value={d.value}>{d.str}</option>)
              ))}
            </select>
          </div>
          <div>
            <i className={`fa fa-clock-o ${styles.timeClock}`} />
            <span className={styles.timeForecast}>
              &nbsp;Ends&nbsp;
              {stopTime}
            </span>
          </div>
          {outletID === 'fertigation' && flagsState[0] === 'fertigation_master' && (
            <div>
              <i className={`fa fa-clock-o ${styles.timeClock}`} />
              <span className={styles.timeForecast}>
                &nbsp;Fertigation Duration&nbsp;
                {fertigationDuration}
                &nbsp;Mins
              </span>
            </div>
          )}
        </div>

        <div className={styles.event_inputs_label}>
          Ends On
          <select id='flags' value={noLaterEnabledState} onChange={handleNoLaterEnabled}>
            <option key='never' value={false}>Never</option>
            <option key='date' value>Date</option>
          </select>
          {noLaterEnabledState &&
            (
              <div>
                <input id='noLaterDate' type='date' value={formatDate(noLaterState)} onChange={handleNoLaterChanged} />
                <input id='noLaterTime' type='time' value={formatHourMin(noLaterState)} onChange={handleNoLaterChanged} />
              </div>
            )}
        </div>

        {outletID === 'fertigation' && (
          <div className={styles.event_inputs_label}>
            Mode
            <select id='flags' value={flagsState[0]} onChange={handleFlagsChange}>
              <option key='normal' value=''>Normal</option>
              <option key='fertigation' value='fertigation_master'>Fertigation</option>
            </select>
            {flagsState[0] === 'fertigation_master' &&
            (
              <div>
                <select id='fertigationDifference0' value={fertigationDifferenceState[0]} onChange={handleFertigationDifference}>
                  {getMinDropdown(0, 61, ' Pre-Flush').map((d) => (
                    (<option key={d.key} value={d.value}>{d.str}</option>)
                  ))}
                </select>
                <select id='fertigationDifference1' value={fertigationDifferenceState[1]} onChange={handleFertigationDifference}>
                  {getMinDropdown(0, 61, ' Post-Flush').map((d) => (
                    (<option key={d.key} value={d.value}>{d.str}</option>)
                  ))}
                </select>
              </div>
            )}
          </div>
        )}
      </div>
      <div>
        {startTimeValid && durationValid && fertigationValid && dayValid
          ? <button type='button' className={styles.save_button} onClick={(updateIndex >= 0) ? () => { modifyEvent(updateIndex) } : createEvent}>{(updateIndex >= 0) ? 'EDIT' : 'ADD'}</button>
          : ''}
        {updateIndex >= 0
          ? <button type='button' className={styles.remove_button} onClick={() => { removeEvent({ outletID, key: updateIndex }) }}>REMOVE</button>
          : ''}
      </div>
    </div>
  )
}

Eventform.propTypes = {
  addEvent: PropTypes.func.isRequired,
  updateEvent: PropTypes.func.isRequired,
  removeEvent: PropTypes.func.isRequired,
  updateIndex: PropTypes.number.isRequired,
  eventFormDay: PropTypes.number.isRequired,
}

export default Eventform
